/**
 * Query Key Factory
 * 
 * Centralized query key management for TanStack Query.
 * Ensures consistent cache invalidation and query organization.
 * 
 * @see https://tanstack.com/query/latest/docs/framework/react/community/lukemorales-query-key-factory
 */

import type { RAListParams } from './raClient'

// ============================================================================
// Types
// ============================================================================

export interface QueryKeyFactory {
  all: readonly [string]
  lists: () => readonly [string, 'list']
  list: (params?: RAListParams) => readonly [string, 'list', RAListParams]
  details: () => readonly [string, 'detail']
  detail: (id: string | number) => readonly [string, 'detail', string | number]
  byIds: (ids: (string | number)[]) => readonly [string, 'byIds', (string | number)[]]
}

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create a query key factory for a resource
 * 
 * @example
 * ```typescript
 * const noteKeys = createQueryKeys('notes')
 * 
 * // Get all keys (for broad invalidation)
 * noteKeys.all // ['notes']
 * 
 * // Get list keys (for list invalidation)
 * noteKeys.lists() // ['notes', 'list']
 * noteKeys.list({ page: 1, filter: { status: 'open' } }) // ['notes', 'list', { page: 1, filter: { status: 'open' } }]
 * 
 * // Get detail keys
 * noteKeys.details() // ['notes', 'detail']
 * noteKeys.detail(123) // ['notes', 'detail', 123]
 * 
 * // Get byIds keys (for reference fields)
 * noteKeys.byIds([1, 2, 3]) // ['notes', 'byIds', [1, 2, 3]]
 * ```
 */
export function createQueryKeys(resource: string): QueryKeyFactory {
  return {
    all: [resource] as const,
    lists: () => [resource, 'list'] as const,
    list: (params: RAListParams = {}) => [resource, 'list', params] as const,
    details: () => [resource, 'detail'] as const,
    detail: (id: string | number) => [resource, 'detail', id] as const,
    byIds: (ids: (string | number)[]) => [resource, 'byIds', ids] as const,
  }
}

// ============================================================================
// Pre-defined Query Keys for Common Resources
// ============================================================================

export const noteKeys = createQueryKeys('conversation_notes')
export const conversationKeys = createQueryKeys('conversations')
export const assistantKeys = createQueryKeys('assistants')
export const customerKeys = createQueryKeys('customers')
export const userKeys = createQueryKeys('users')
export const teamKeys = createQueryKeys('teams')
export const partnerKeys = createQueryKeys('partners')
